import { upperFirst, lowerCase, isFinite } from 'lodash';
import { isAfter } from 'date-fns';
import * as yup from 'yup';
import { setIn } from 'final-form';
import { months } from 'src/constants';

export const Yup = yup;

export function escapeRegExp(text) {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

Yup.setLocale({
  mixed: {
    notType: function notType({ type, path }) {
      switch (type) {
        case 'number':
          return `${path} must be a number`;
        case 'string':
          return `${path} must be a string`;
        default:
          return 'Wrong type';
      }
    },
    required: ({ path }) => {
      const transformedPath = upperFirst(lowerCase(path));
      return `${transformedPath} can't be blank`;
    },
    defined: ({ path }) => {
      const transformedPath = upperFirst(lowerCase(path));
      return `${transformedPath} can't be blank`;
    }
  }
});

//for integration with react-final-form
export const validate = async (values, schema) => {
  if (typeof schema === 'function') {
    schema = schema();
  }

  try {
    await schema.validate(values, { abortEarly: false });
  } catch (e) {
    return e.inner.reduce((errors, error) => setIn(errors, error.path, error.message), {});
  }
};

/* eslint-disable no-template-curly-in-string */
function maxWords(number: number, path = '${path}') {
  return this.test('maxWords', `${path} should not contain more than ${number} words`, function(str: string) {
    const wordsNumber = str?.split(' ').length || 0;
    return wordsNumber <= number;
  });
}
Yup.addMethod(Yup.string, 'maxWords', maxWords);

/* eslint-disable no-template-curly-in-string */
function equalTo(ref, path, reference) {
  return Yup.mixed().test({
    name: 'equalTo',
    exclusive: false,
    message: `${path || '${path}'} must be the same as ${reference || '${reference}'}`,
    params: {
      reference: ref.path
    },
    test(value) {
      return value === this.resolve(ref);
    }
  });
}
Yup.addMethod(Yup.string, 'equalTo', equalTo);

export const emailRegExp = new RegExp(/^([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z]{2,})$/i);
Yup.addMethod(Yup.string, 'customEmail', function(ref, path) {
  return this.test({
    name: 'customEmail',
    exclusive: false,
    message: `${path || '${path}'} must be a valid email`,
    test(value) {
      return emailRegExp.test(value);
    }
  });
});

export const customEmail = Yup.string().customEmail();

export const getEmailRegExpWithSpecificDomains = (domains, strict = true) =>
  new RegExp(`^\\S+@(${domains.map(escapeRegExp).join('|')})${strict ? '$' : '\\.'}`, 'i');

export const oneOfDomain = function(allowedDomains) {
  return this.test('', 'Invalid email domain.', function(email) {
    if (!email) return true;
    const regExp = getEmailRegExpWithSpecificDomains(allowedDomains);
    return regExp.test(email);
  });
};
Yup.addMethod(Yup.string, 'oneOfDomain', oneOfDomain);

const oneOfFileExtension = function(allowedExtensions, fieldName = 'upl_file') {
  return this.test('', 'Invalid file extension. Please use allowed ones.', function(formData) {
    if (!formData) return true;
    const fileName = formData.get(fieldName).name;
    const fileExtension = fileName.split('.').reverse()[0];
    return allowedExtensions.includes(fileExtension);
  });
};
Yup.addMethod(Yup.mixed, 'oneOfFileExtension', oneOfFileExtension);

const isLater = function(fieldName: string, errorMessage = 'End date must be later than the start date') {
  return this.test('isLater', '', function(endDate) {
    const startDate = this.resolve(Yup.ref(fieldName));
    if (!startDate?.year || !startDate?.month) return true;

    let endDateObj;
    if (endDate?.month === 'Current') {
      endDateObj = new Date();
    } else if (endDate?.year && endDate?.month) {
      endDateObj = new Date(endDate.year, months.findIndex(m => m === endDate.month));
    } else {
      return true;
    }

    const startDateObj = new Date(startDate.year, months.findIndex(m => m === startDate.month));

    const isValid = isAfter(endDateObj, startDateObj);
    if (!isValid) {
      const errors: any = [
        new Yup.ValidationError(' ', endDate.month, `${this.path}.month`),
        new Yup.ValidationError(' ', endDate.year, `${this.path}.year`),
        new Yup.ValidationError(errorMessage, endDate, `${this.path}.global`)
      ];
      throw new Yup.ValidationError(errors);
    }

    return true;
  });
};
Yup.addMethod(Yup.object, 'isLater', isLater);

const sumIsEquelTo = function(
  firstFieldName: string,
  secondFieldName: string,
  errorFieldName: string,
  sum: number,
  message
) {
  return this.test('sumIsEquelTo', '', function(value) {
    const firstField = value[firstFieldName];
    const secondField = value[secondFieldName];
    if ((!isFinite(firstField) && !isFinite(secondField)) || firstField + secondField === sum) {
      return true;
    }
    throw this.createError({ path: errorFieldName, message });
  });
};
Yup.addMethod(Yup.object, 'sumIsEquelTo', sumIsEquelTo);

export const email = Yup.string()
  .customEmail()
  .required();

export const password = Yup.string()
  .min(8, 'Password is too short (minimum is 8 characters)')
  .required();

export const password_confirmation = Yup.string()
  .equalTo(Yup.ref('password'), 'Password confirmation')
  .required();

// eslint-disable-next-line
export const wrapSubmit = (onSubmit, options = { onError: v => v }) => async values => {
  try {
    await onSubmit(values);
  } catch ({ error, payload }) {
    if (Array.isArray(payload)) {
      options.onError(payload.join('. '));
    }
    return { error, payload };
  }
};

// caution: hacky hack, let me know if you have better idea
export const disableValidationIfEntityDestoyed = <T>(schema: T): T =>
  schema
    .nullable()
    .clone()
    .transform(function(value, originalValue) {
      if (originalValue._destroy) return null;
      return value;
    });

export const validateFormikField = schema => value => {
  try {
    // passing down null somehow brokes validator and it says must be a a string
    schema.validateSync(value === null ? undefined : value);
    return;
  } catch (error) {
    return error?.message;
  }
};
